import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { UseOutletContextStatement } from './Statement';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { TicketEntryDA } from '../../../../services/admin/ticket.entry.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { StatementDA } from '../../../../services/admin/statement.service';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { resolveDocumentRoute } from '../../../../scripts/document.link.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppPreloader from '../../../../components/Preloader';

interface TicketItem {
  id: string;
  code: string;
  formLine: string | null;
  side: 'sale' | 'purchase';
  postingNature: 'credit' | 'debit';
  rateApplied: number;
  base: number;
  amount: number;
  created_date: string;
  document_number: string | null;
  id_entry: string | null;
  id_period: string | null;
  id_invoice: string | null;
  id_provider_invoice: string | null;
  id_expense: string | null;
}

interface EligibilityInfo {
  eligible: boolean;
  reason?: string;
  orphan_count?: number;
  correction_period_id?: string;
}

function resolveTicketDocRoute(t: TicketItem): string | null {
  if (t.id_invoice) return resolveDocumentRoute('invoice', t.id_invoice);
  if (t.id_provider_invoice) return resolveDocumentRoute('provider_invoice', t.id_provider_invoice);
  if (t.id_expense) return resolveDocumentRoute('expense', t.id_expense);
  if (t.id_entry) return resolveDocumentRoute('entry', t.id_entry);
  return null;
}

function findFiscalYear(date: string, fiscalYears: any[]): any | null {
  if (!date) return null;
  const d = date.substring(0, 10);
  for (const fy of fiscalYears) {
    const start = (fy.start_date ?? '').substring(0, 10);
    const end = (fy.end_date ?? '').substring(0, 10);
    if (start && end && d >= start && d <= end) return fy;
  }
  return null;
}

const TicketPendingListPage: React.FunctionComponent = () => {
  const { setRoute, companyForUser } = UseOutletContextStatement();
  const { lang } = StateLanguage();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);
  const [tickets, setTickets] = useState<TicketItem[] | null>(null);
  const [orphanOnly, setOrphanOnly] = useState(true);
  const [sideFilter, setSideFilter] = useState('');
  const [fiscalYears, setFiscalYears] = useState<any[]>([]);
  const [eligibilityMap, setEligibilityMap] = useState<Record<string, EligibilityInfo>>({});
  const [creatingCorrection, setCreatingCorrection] = useState(false);

  const loadTickets = async (id_company: string) => {
    setTickets(null);
    await TicketEntryDA.getPendingTickets(id_company, { orphanOnly, side: sideFilter || undefined }).then(response => {
      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
        setTickets(data);
      } else {
        errorWebHandler(lang, response);
        setTickets([]);
      }
    }).catch(error => {
      errorWebHandler(lang, error);
      setTickets([]);
    });
  };

  const loadFiscalYears = async (id_company: string) => {
    try {
      const response = await FiscalYearDA.getFiscalYears(id_company);
      if (response.status === 200) {
        const data = response.data;
        const items = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? [];
        setFiscalYears(items);
        for (const fy of items) {
          const fyId = fy.id ?? fy._id;
          if (fyId) loadEligibility(id_company, fyId);
        }
      }
    } catch {
      // silent
    }
  };

  const loadEligibility = async (id_company: string, id_fiscal_year: string) => {
    try {
      const response = await StatementDA.getAnnualCorrectionEligibility(id_company, id_fiscal_year);
      if (response.status === 200 && response.data) {
        setEligibilityMap(prev => ({ ...prev, [id_fiscal_year]: response.data }));
      }
    } catch {
      // silent
    }
  };

  const handleCreateCorrection = async (fyId: string, fyName: string) => {
    if (!companyForUser) return;

    const result = await GlobalSwal.fire({
      title: `${lang.labels.createAnnualCorrection} — ${fyName}`,
      text: lang.labels.confirmCreateAnnualCorrection,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.create,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed) return;

    setCreatingCorrection(true);
    try {
      const response = await StatementDA.createAnnualCorrection(companyForUser.id, fyId);
      if (response.status === 200 || response.status === 201) {
        const correctionPeriod = response.data;
        const correctionId = correctionPeriod?.id ?? correctionPeriod?._id;
        if (correctionId) {
          navigate(`/main/accounting/vat-return/view/${correctionId}`);
        } else {
          // Reload to reflect changes
          await loadTickets(companyForUser.id);
          await loadFiscalYears(companyForUser.id);
        }
      } else {
        const error = response.data as any;
        GlobalSwal.fire({ title: error?.message ?? getError(error?.code, lang.code, error?.message), icon: 'error' });
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' });
    } finally {
      setCreatingCorrection(false);
    }
  };

  const correctionButton = (fyId: string, fyName: string) => {
    const elig = eligibilityMap[fyId];
    if (!elig) return null;

    if (elig.eligible) {
      return (
        <button
          className="btn btn-sm btn-light-success py-1 px-3"
          onClick={() => handleCreateCorrection(fyId, fyName)}
          disabled={creatingCorrection}
        >
          {creatingCorrection
            ? <span className="spinner-border spinner-border-sm align-middle"></span>
            : `${lang.labels.createAnnualCorrection} (${elig.orphan_count ?? 0})`
          }
        </button>
      );
    }

    if (elig.reason === 'already_exists' && elig.correction_period_id) {
      return (
        <Link to={`/main/accounting/vat-return/view/${elig.correction_period_id}`} className="btn btn-sm btn-light-primary py-1 px-3">
          {lang.labels.viewAnnualCorrection}
        </Link>
      );
    }

    const reasonText = elig.reason === 'periods_not_all_filed' ? lang.labels.periodsNotAllFiled
      : elig.reason === 'no_orphans' ? lang.labels.noOrphans
      : elig.reason === 'no_standard_period' ? lang.labels.noOrphans
      : '';

    if (reasonText) {
      return (
        <span className="text-muted fs-8" title={reasonText}>{reasonText}</span>
      );
    }

    return null;
  };

  useEffect(() => {
    if (companyForUser) {
      loadTickets(companyForUser.id);
      loadFiscalYears(companyForUser.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, orphanOnly, sideFilter]);

  useEffect(() => {
    setMounted(true);
    setRoute({ path: { root: lang.labels.accounting, branch: lang.labels.pendingVatTickets }, company: true });
    return () => setMounted(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) return null;

  // Group tickets by fiscal year
  const ticketGroups: Array<{ fyId: string; fyName: string; tickets: TicketItem[] }> = [];
  const ungrouped: TicketItem[] = [];

  if (tickets && fiscalYears.length > 0) {
    const grouped = new Map<string, TicketItem[]>();
    for (const ticket of tickets) {
      const fy = findFiscalYear(ticket.created_date, fiscalYears);
      if (fy) {
        const fyId = fy.id ?? fy._id;
        const list = grouped.get(fyId) || [];
        list.push(ticket);
        grouped.set(fyId, list);
      } else {
        ungrouped.push(ticket);
      }
    }
    for (const fy of fiscalYears) {
      const fyId = fy.id ?? fy._id;
      const group = grouped.get(fyId);
      if (group && group.length > 0) {
        ticketGroups.push({ fyId, fyName: fy.name ?? fyId, tickets: group });
      }
    }
  }

  const hasGrouping = ticketGroups.length > 0;

  const renderTicketRow = (ticket: TicketItem, index: number) => {
    const docRoute = resolveTicketDocRoute(ticket);
    const docLabel = ticket.document_number || '';
    return (
      <tr key={index} className="row-dashed">
        <td className="fs-7">{dateFormat(lang.code, ticket.created_date)}</td>
        <td>
          {docRoute && docLabel
            ?
            <Link to={docRoute} className="text-primary fw-bold text-hover-dark">
              {docLabel}
            </Link>
            :
            docLabel
              ? <span className="text-muted">{docLabel}</span>
              : <span className="text-muted">{'\u2014'}</span>
          }
        </td>
        <td>
          <span className="badge badge-light fw-bold fs-8">{ticket.code}</span>
        </td>
        <td>{ticket.formLine || '\u2014'}</td>
        <td>
          <span className={`badge badge-light-${ticket.side === 'sale' ? 'success' : 'info'} fw-bold fs-8`}>
            {ticket.side === 'sale' ? lang.labels.sale : lang.labels.purchase}
          </span>
        </td>
        <td className="text-end">{formatMoney(ticket.base)}</td>
        <td className="text-end">{formatMoney(ticket.amount)}</td>
        <td className="text-end">{(ticket.rateApplied * 100).toFixed(1)}%</td>
      </tr>
    );
  };

  const tableHeader = (
    <thead className="border-bottom">
      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
        <th className="min-w-100px">{lang.labels.date}</th>
        <th className="min-w-120px">{lang.labels.reference}</th>
        <th className="min-w-80px">{lang.labels.code}</th>
        <th className="min-w-80px">{lang.labels.formLine}</th>
        <th className="min-w-80px">{lang.labels.side}</th>
        <th className="min-w-100px text-end">{lang.labels.base} (CHF)</th>
        <th className="min-w-100px text-end">{lang.labels.vatShort} (CHF)</th>
        <th className="min-w-80px text-end">{lang.labels.taxRate}</th>
      </tr>
    </thead>
  );

  return (
    <div className="w-100 h-100">
      {companyForUser && tickets !== null
        ?
        <>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <h3>{lang.labels.pendingVatTickets}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex align-items-center gap-3">
                  <select
                    className="form-select form-select-sm w-auto"
                    value={sideFilter}
                    onChange={e => setSideFilter(e.target.value)}
                  >
                    <option value="">{lang.labels.all}</option>
                    <option value="sale">{lang.labels.sale}</option>
                    <option value="purchase">{lang.labels.purchase}</option>
                  </select>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="orphanToggle"
                      checked={orphanOnly}
                      onChange={e => setOrphanOnly(e.target.checked)}
                    />
                    <label className="form-check-label text-muted fs-7" htmlFor="orphanToggle">
                      {lang.labels.orphansOnly}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              { hasGrouping
                ? ticketGroups.map(group => (
                  <div key={group.fyId} className="mb-6">
                    <div className="d-flex align-items-center justify-content-between bg-light rounded p-3 mb-3">
                      <span className="fw-bolder fs-7">
                        {lang.labels.fiscalYear}: {group.fyName} — {group.tickets.length} {lang.labels.ticketsPending}
                      </span>
                      <div>{correctionButton(group.fyId, group.fyName)}</div>
                    </div>
                    <div className="dataTables_wrapper">
                      <div className="table-responsive">
                        <table className="table align-middle table-row-dashed mb-2">
                          {tableHeader}
                          <tbody className="text-secondary fw-bold">
                            {group.tickets.map((ticket, index) => renderTicketRow(ticket, index))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))
                :
                <div className="dataTables_wrapper">
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed mb-2">
                      {tableHeader}
                      {tickets.length > 0
                        ?
                        <tbody className="text-secondary fw-bold">
                          {tickets.map((ticket, index) => renderTicketRow(ticket, index))}
                        </tbody>
                        :
                        <tbody>
                          <tr className="text-secondary fw-bold">
                            <td colSpan={8} className="text-center">{lang.labels.noRecordsFound}</td>
                          </tr>
                        </tbody>
                      }
                    </table>
                  </div>
                </div>
              }
              { ungrouped.length > 0 && hasGrouping &&
                <div className="mb-6">
                  <div className="d-flex align-items-center bg-light rounded p-3 mb-3">
                    <span className="fw-bolder fs-7">
                      {ungrouped.length} {lang.labels.ticketsPending}
                    </span>
                  </div>
                  <div className="dataTables_wrapper">
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed mb-2">
                        {tableHeader}
                        <tbody className="text-secondary fw-bold">
                          {ungrouped.map((ticket, index) => renderTicketRow(ticket, index))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              }
              {tickets.length > 0 &&
                <div className="text-muted fs-7 mt-2">
                  {tickets.length} ticket(s)
                </div>
              }
            </div>
          </div>
        </>
        :
        <AppPreloader />
      }
    </div>
  );
};

export default TicketPendingListPage;
